import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Eye, EyeOff, Volume2 } from "lucide-react";
import { useLang, loc } from "@/lib/content";
import { api } from "@/lib/api";
import { speak } from "@/lib/tts";

export default function N400Simulator() {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [secIdx, setSecIdx] = useState(0);
  const [pos, setPos] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api("/study/n400").then(setData).catch(() => setData({ sections: [] }));
  }, []);

  const sections = data?.sections || [];
  const section = sections[secIdx];
  const item = section?.items[pos];
  const otherLang = lang === "es" ? "en" : "es";

  const play = async (text, key) => {
    setBusy(key);
    try {
      const a = await speak(text);
      a.onended = () => setBusy("");
    } catch (e) {
      setBusy("");
    }
  };

  const go = (dir) => {
    const nextPos = pos + dir;
    if (nextPos < 0 || nextPos >= (section?.items.length || 0)) return;
    setPos(nextPos);
    setShowAnswer(false);
  };

  const flatCount = useMemo(() => sections.reduce((a, s) => a + s.items.length, 0), [sections]);

  return (
    <section id="n400" className="bg-paper py-24">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-heritage">
            {lang === "es" ? "Simulador con audio" : "Audio simulator"}
          </p>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl">
            {lang === "es" ? "La entrevista N-400, " : "The N-400 interview, "}
            <span className="italic text-heritage">{lang === "es" ? "pregunta por pregunta" : "question by question"}</span>
          </h2>
          <p className="mt-4 text-inkbody">
            {lang === "es"
              ? `Las ${flatCount} preguntas de la entrevista con respuestas modelo y audio en español e inglés. Escucha, responde en voz alta y compara.`
              : `All ${flatCount} interview questions with model answers and audio in English and Spanish. Listen, answer out loud, and compare.`}
          </p>
        </motion.div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
          {sections.map((s, i) => (
            <button
              key={s.key}
              data-testid={`n400-tab-${s.key}`}
              onClick={() => {
                setSecIdx(i);
                setPos(0);
                setShowAnswer(false);
              }}
              className={`rounded-full px-4 py-2 text-xs font-bold transition-all ${i === secIdx ? "bg-navy text-paper" : "border border-line bg-white text-navy hover:border-navy/40"}`}
            >
              {loc(s.title, lang)}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {item && (
            <motion.div
              key={secIdx + "-" + pos}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.3 }}
              data-testid="n400-card"
              className="mx-auto mt-8 max-w-3xl rounded-3xl border border-line bg-white p-7 shadow-xl sm:p-9"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-heritage">
                  {loc(section.title, lang)} · {pos + 1} / {section.items.length}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="n400-audio-es"
                    onClick={() => play(item.q.es, "q-es")}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-bold transition-all ${busy === "q-es" ? "border-heritage bg-heritage/10 text-heritage" : "border-line text-navy hover:border-navy/40"}`}
                  >
                    <Volume2 size={13} /> ES
                  </button>
                  <button
                    data-testid="n400-audio-en"
                    onClick={() => play(item.q.en, "q-en")}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-bold transition-all ${busy === "q-en" ? "border-heritage bg-heritage/10 text-heritage" : "border-line text-navy hover:border-navy/40"}`}
                  >
                    <Volume2 size={13} /> EN
                  </button>
                </div>
              </div>

              <p className="mt-6 font-display text-2xl font-semibold leading-snug text-navy sm:text-3xl" data-testid="n400-question">
                “{loc(item.q, lang)}”
              </p>

              {showAnswer ? (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-2xl bg-paperdeep p-5" data-testid="n400-answer">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-emeraldsucc">
                      {lang === "es" ? "Respuesta modelo" : "Model answer"}
                    </span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => play(item.a.es, "a-es")} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[11px] font-bold ${busy === "a-es" ? "border-emeraldsucc text-emeraldsucc" : "border-line text-navy"}`}>
                        <Volume2 size={12} /> ES
                      </button>
                      <button onClick={() => play(item.a.en, "a-en")} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[11px] font-bold ${busy === "a-en" ? "border-emeraldsucc text-emeraldsucc" : "border-line text-navy"}`}>
                        <Volume2 size={12} /> EN
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 text-base font-semibold leading-relaxed text-navy">{loc(item.a, lang)}</p>
                  <p className="mt-2 text-sm leading-relaxed text-mutedtext">{loc(item.a, otherLang)}</p>
                </motion.div>
              ) : (
                <p className="mt-6 text-sm text-mutedtext">{lang === "es" ? "Responde en voz alta primero — luego compara con la respuesta modelo." : "Answer out loud first — then compare with the model answer."}</p>
              )}

              <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
                <button
                  data-testid="n400-prev"
                  onClick={() => go(-1)}
                  disabled={pos === 0}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line px-5 py-2.5 text-xs font-bold text-navy disabled:opacity-40"
                >
                  <ArrowLeft size={13} /> {lang === "es" ? "Anterior" : "Previous"}
                </button>
                <button
                  data-testid="n400-toggle-answer"
                  onClick={() => setShowAnswer((v) => !v)}
                  className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-2.5 text-xs font-bold text-paper"
                >
                  {showAnswer ? <EyeOff size={13} /> : <Eye size={13} />}
                  {showAnswer ? (lang === "es" ? "Ocultar respuesta" : "Hide answer") : lang === "es" ? "Ver respuesta modelo" : "Show model answer"}
                </button>
                <button
                  data-testid="n400-next"
                  onClick={() => go(1)}
                  disabled={pos >= section.items.length - 1}
                  className="inline-flex items-center gap-1.5 rounded-full bg-heritage px-5 py-2.5 text-xs font-bold text-paper disabled:opacity-40"
                >
                  {lang === "es" ? "Siguiente" : "Next"} <ArrowRight size={13} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
